import { useMemo } from "react";

import { CameraIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import Modal from "../../layouts/Modal";
import { IPart, IPartImageType } from "../../models/Part";
import EbayLinkButton from "../shared/Inputs/EbayLinkButton";

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  part: IPart;
  mpn: string;
  setMpn: (value: string) => void;
  onMpnBlur: (value: string) => void;
};

export default function StockMpnImageModal({
  open,
  setOpen,
  part,
  mpn,
  setMpn,
  onMpnBlur,
}: Props) {
  const { t } = useTranslation("stock");
  const mpnImage = useMemo(
    () => part.images?.find((image) => image.imageType === IPartImageType.MPN),
    [part.images]
  );

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-[96vw] w-[96vw]">
      <div
        className="relative flex max-h-[90vh] flex-col overflow-hidden p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
          }}
          className="absolute right-1 top-1 z-10 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          aria-label={t("Close")}
          title={t("Close")}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden">
          {mpnImage ? (
            <img
              className="mx-auto block max-h-[calc(90vh-10rem)] max-w-[calc(96vw-2rem)] object-contain"
              src={mpnImage.url}
              alt="MPN"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 py-8 text-slate-500">
              <CameraIcon className="h-12 w-12" />
              <p className="text-sm">{t("No MPN image on this part.")}</p>
            </div>
          )}
        </div>

        <div className="mt-4 shrink-0 border-t border-slate-200/80 pt-4">
          <div className="mx-auto w-full max-w-xl rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white px-4 py-3 shadow-sm ring-1 ring-slate-200/50">
            <label
              htmlFor={`mpn-modal-${part.id}`}
              className="mb-2 block text-[10px] font-semibold uppercase tracking-wide text-slate-500"
            >
              {t("Manufacturer Part Number")}
            </label>
            <div className="flex items-center gap-2">
              <div className="flex min-w-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm focus-within:border-primary-400 focus-within:ring-1 focus-within:ring-primary-400">
                <input
                  id={`mpn-modal-${part.id}`}
                  type="text"
                  value={mpn}
                  onChange={(e) => setMpn(e.target.value)}
                  onBlur={(e) => onMpnBlur(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  placeholder={t("Enter MPN")}
                  className="min-w-0 w-full border-0 bg-transparent px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                />
              </div>
              {mpn.trim() ? <EbayLinkButton query={mpn.trim()} /> : null}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
